// Magazine-style PDF/print HTML template. Pure string-builder: called
// server-side by the Worker queue consumer when generating the DocRaptor
// PDF, and client-side by /app/report/print which drops the same HTML
// into a dangerouslySetInnerHTML container for Ctrl+P.
//
// No JS is executed by DocRaptor (javascript: false), so everything that
// needs to render must be expressed as static HTML + inline CSS + web
// fonts loaded via <link>. FontAwesome is out; Tailwind is out. We use
// Hoefler's Gotham + Quarto (the same account/css URL the existing
// sked-insights-backend uses) so typography stays consistent with the
// in-app surfaces.

import type { ReportPayload } from "../../server/ga4/report-schema";
import type { InsightsV2 } from "../insights/schema";
import { buildSkedHref } from "../leadgen/cta-rules";
import { dayCount, formatTableRowDate } from "./date-range";
import { buildDepthSvg, type DepthSvgPoint } from "./depth-svg";
import {
  buildDeviceMixAreaSvg,
  buildDeviceMixDonutSvg,
  DEVICE_COLOR,
  DEVICE_LABEL,
  DEVICE_RENDER_ORDER,
  type DeviceKey,
} from "./device-mix-svg";
import { buildSourceMixSvg } from "./source-mix-svg";
import { buildEcommerceSvg } from "./ecommerce-svg";
import { buildTimeSeriesSvg } from "./time-series-svg";

// Single point of style truth for the generated document. Kept as a
// constant string so DocRaptor's prince engine sees the same output
// bytes every render, and so the browser print page can reuse it.
const PRINT_CSS = `
@page { size: A4; margin: 0; -webkit-print-color-adjust: exact; }
* { box-sizing: border-box; }
html, body {
  margin: 0;
  padding: 0;
  font-family: "Gotham SSm A", "Gotham SSm B", -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0F0F14;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.serif { font-family: "Quarto A", "Quarto B", Georgia, "Times New Roman", serif; }
.page {
  position: relative;
  width: 210mm;
  min-height: 297mm;
  padding: 18mm 20mm 22mm;
  page-break-after: always;
  break-after: page;
}
.page:last-of-type { page-break-after: auto; break-after: auto; }
.page.cover {
  min-height: 297mm;
  height: 297mm;
}
.cover {
  background: #0B1120;
  color: #FFFFFF;
  padding: 20mm 20mm 14mm;
}
.cover .eyebrow {
  font-family: "Quarto A", "Quarto B", Georgia, serif;
  font-style: italic;
  color: #B9ADFF;
  font-size: 14pt;
  letter-spacing: 0.01em;
}
.cover .hero-number {
  font-weight: 900;
  font-size: 128pt;
  line-height: 1;
  letter-spacing: -0.04em;
  margin-top: 16pt;
  color: #FFFFFF;
  font-variant-numeric: tabular-nums;
}
.cover .hero-label {
  font-size: 11pt;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #9583FF;
  margin-top: 10pt;
}
.cover .property-name {
  font-size: 28pt;
  font-weight: 900;
  letter-spacing: -0.025em;
  margin-top: 28pt;
  color: #FFFFFF;
}
.cover .property-name em {
  display: block;
  font-family: "Quarto A", "Quarto B", Georgia, serif;
  font-style: italic;
  font-weight: 600;
  color: #9583FF;
  margin-top: 8pt;
}
.cover .period {
  font-size: 12pt;
  color: #DCD6FF;
  margin-top: 22pt;
}
.cover .footer-row {
  position: absolute;
  right: 20mm;
  bottom: 14mm;
}
.sked-mark {
  display: inline-block;
  text-decoration: none;
}
.sked-mark img {
  display: block;
  width: 44mm;
  height: auto;
}
.page-logo-link {
  display: inline-block;
  text-decoration: none;
}
.page-logo-link img {
  display: block;
  width: 20mm;
  height: auto;
}

.section-header {
  border-bottom: 1px solid #DCD6FF;
  padding-bottom: 6pt;
  margin-bottom: 14pt;
}
.section-header .eyebrow {
  font-size: 9pt;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #0E1680;
}
.section-header .accent-bar {
  display: block;
  width: 20pt;
  height: 3pt;
  background: #4F31FF;
  border-radius: 2pt;
  margin: 5pt 0 0;
}
.section-header h2 {
  margin: 8pt 0 0;
  font-size: 26pt;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #0F0F14;
}
.section-header h2 em {
  font-family: "Quarto A", "Quarto B", Georgia, serif;
  font-style: italic;
  font-weight: 600;
  font-size: 1.08em;
  line-height: 1;
  color: #0E1680;
}
.cover .property-name em {
  font-size: 1.06em;
}

/* Real HTML <table> for the KPI grid. We use \`border-collapse: collapse\`
   so there's zero intrinsic spacing (\`border-spacing\` bleeds into outer
   edges in Prince and nudges the first/last cells narrower than the
   middle ones). The 6pt visual gap between cards comes from matched
   3pt left/right TD padding — every cell gets the same padding, so
   every inner card is exactly the same width. */
.kpi-grid {
  table-layout: fixed;
  width: 100%;
  border-collapse: collapse;
  margin: 0 0 42pt;
  padding: 0;
}
/* The \`height: 1px\` trick — Chrome only propagates percentage heights
   down from <td> to <div> children when the row itself has a defined
   height. 1px is small enough that it never actually constrains the
   row (intrinsic content height always wins), but it flips the "height
   is defined" flag so \`height: 100%\` on the card below stretches it
   to fill the row. Prince honours this identically. */
.kpi-grid tr { height: 1px; }
.kpi {
  vertical-align: top;
  padding: 0 3pt;
  height: 100%;
}
.kpi-card {
  height: 100%;
  box-sizing: border-box;
  background: #F4F2FF;
  border: 1px solid #DCD6FF;
  border-radius: 6pt;
  padding: 10pt 12pt;
}
.kpi .label {
  font-size: 8pt;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #201466;
}
.kpi .value {
  margin-top: 6pt;
  font-size: 17pt;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #0F0F14;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.kpi-grid.three .kpi .value { font-size: 22pt; }
.kpi .delta {
  margin-top: 4pt;
  font-size: 10pt;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.kpi .delta.up { color: #047857; }
.kpi .delta.down { color: #B91C1C; }
/* Invisible placeholder so cards without a real delta keep the same
   vertical rhythm as cards that do, independent of any \`height: 100%\`
   trickery (which is unreliable on <td> children in Chrome). */
.kpi .delta.placeholder { visibility: hidden; }

table.data {
  width: 100%;
  border-collapse: collapse;
  margin-top: 8pt;
  font-size: 10pt;
}
table.data thead th {
  text-align: left;
  font-size: 8pt;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #201466;
  border-bottom: 1px solid #DCD6FF;
  padding: 8pt 6pt;
}
table.data tbody td {
  border-bottom: 1px solid #EDEAFF;
  padding: 8pt 6pt;
  color: #0F0F14;
  font-variant-numeric: tabular-nums;
}
table.data tbody tr:last-child td { border-bottom: none; }

.exec-summary ul { list-style: none; padding: 0; margin: 0; }
.exec-summary li {
  position: relative;
  padding: 10pt 12pt 10pt 28pt;
  background: #FCFAFF;
  border: 1px solid #DCD6FF;
  border-radius: 6pt;
  margin-bottom: 6pt;
}
.exec-summary .severity {
  position: absolute;
  left: 12pt;
  top: 14pt;
  width: 8pt;
  height: 8pt;
  border-radius: 100%;
}
.exec-summary .severity.severe { background: #DC2626; }
.exec-summary .severity.medium { background: #F59E0B; }
.exec-summary .severity.mild   { background: #9583FF; }
.exec-summary .cat {
  font-size: 8pt;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #201466;
}
.exec-summary .msg {
  margin-top: 4pt;
  font-size: 10pt;
  color: #0F0F14;
  line-height: 1.5;
}

/* Footer uses floats (not flex/table) so Prince 10.1 reliably pins the
   logo to the far right on every page. \`overflow: hidden\` on the parent
   contains the floats so the fixed bottom-position is preserved. */
.print-footer {
  position: absolute;
  bottom: 10mm;
  left: 15mm;
  right: 15mm;
  overflow: hidden;
  font-size: 7pt;
  color: #9583FF;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.print-footer > span {
  float: left;
  max-width: 78%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
}
.print-footer > a {
  float: right;
  line-height: 1;
}
`;

const PDF_FOOTER_LOGO_HREF = buildSkedHref({ medium: "pdf_report", campaign: "footer_logo" });
const PDF_COVER_LOGO_HREF = buildSkedHref({ medium: "pdf_report", campaign: "cover_logo" });

function skedLogoImg(src: string): string {
  // Escape the src even though it's env-controlled today — defence in depth
  // against any future callsite that lets the value be user-influenced.
  return `<img src="${escapeHtml(src)}" alt="Sked Social">`;
}
function footerLogo(src: string): string {
  return `<a href="${escapeHtml(PDF_FOOTER_LOGO_HREF)}" class="page-logo-link" aria-label="Sked Social">${skedLogoImg(src)}</a>`;
}

function escapeHtml(raw: string | number | null | undefined): string {
  if (raw === null || raw === undefined) return "";
  return String(raw)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Split the LLM's paragraph body on blank lines (`\n\n`) so the print
// renderer emits one short <p> per sentence. Matches the prompt
// contract (2–3 one-sentence paragraphs) and the parser's output shape.
function splitInsightsParagraphs(paragraph: string): string[] {
  return paragraph
    .split(/\n\s*\n/)
    .map((p) => p.replace(/\s+/g, " ").trim())
    .filter(Boolean);
}

// Escape the text first, then unwrap `*text*` markers into a
// purple serif-italic <em> accent. Escaping before the regex replace is
// safe because `*` is not HTML-escaped and the inner content was
// already escaped by the first pass.
function renderInsightsEmphasis(text: string): string {
  return escapeHtml(text).replace(
    /\*([^*\n]+)\*/g,
    "<em style=\"font-family:Georgia,'Times New Roman',serif;font-style:italic;font-weight:600;color:#4A2AB5;\">$1</em>",
  );
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
}

function formatPercent(ratio: number): string {
  return `${(ratio * 100).toFixed(1)}%`;
}

function formatCurrency(n: number): string {
  // Force en-US so we get the short "$12,430" form. Without a locale
  // pin, Node picks up the host system locale — which can render USD as
  // "USD 12,430" and overflow the KPI cell.
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

/** Compact forms for KPI cells. 10 000 → "10K", 1 200 000 → "1.2M".
 *  Keeps short values in their full form so we don't lose precision
 *  where the cell has room to spare. */
function formatCompactNumber(n: number): string {
  if (Math.abs(n) < 10000) return formatNumber(n);
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(n);
}

function formatCompactCurrency(n: number): string {
  if (Math.abs(n) < 10000) return formatCurrency(n);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(n);
}

function formatDelta(d: number | null): string {
  if (d === null) return "—";
  const sign = d >= 0 ? "+" : "";
  return `${sign}${(d * 100).toFixed(1)}%`;
}

/** Emits the KPI delta line with a neutral class when the delta is
 *  null so we don't render a green "—" (up) for a missing comparison. */
function renderDelta(d: number | null): string {
  if (d === null) return `<div class="delta">${formatDelta(d)}</div>`;
  return `<div class="delta ${d >= 0 ? "up" : "down"}">${formatDelta(d)}</div>`;
}

function rangeLabel(range: ReportPayload["meta"]["range"]): string {
  if (range === "last_7d") return "Last 7 days";
  if (range === "last_90d") return "Last 90 days";
  return "Last 28 days";
}

function formatIsoDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export interface PrintHtmlArgs {
  payload: ReportPayload;
  propertyLabel: string;
  /** Absolute or path-relative URL to the Sked Social block logo. Must
   *  be reachable by DocRaptor's renderer (so in the Worker this should
   *  be an absolute URL pinned to the current APP_BASE_URL). */
  logoUrl: string;
  /** M13.6 — frozen AI insights generated by the export-consumer
   *  before rendering. Null when the LLM call failed; we render a
   *  placeholder page so the PDF still ships. */
  insights?: { paragraph: string; bullets: string[] } | null;
  /** SKE-3454 — structured V2 insights carrying `shareableTLDR`. When
   *  present the insights page leads with the TL;DR as a subhead, then
   *  the expanded paragraphs, then the action list. Falls back to the
   *  legacy paragraph render when absent (pre-SKE-3454 cached payloads). */
  insightsV2?: InsightsV2 | null;
}

export function buildPrintHtml({
  payload,
  propertyLabel,
  logoUrl,
  insights = null,
  insightsV2 = null,
}: PrintHtmlArgs): string {
  const SKED_LOGO_IMG = skedLogoImg(logoUrl);
  const FOOTER_LOGO = footerLogo(logoUrl);
  const { overview, perPlatform, perCountry, landingPages, recommendations, meta } = payload;
  const showRevenue = overview.totalRevenue > 0;
  const hasCustomConversions = overview.customConversions !== undefined;
  const compareActive = meta.compare !== "none";
  const periodLabel = `${rangeLabel(meta.range)} · ${formatIsoDate(meta.periodStart)} → ${formatIsoDate(meta.periodEnd)}`;
  const compareLabel = meta.comparePeriodStart
    ? `vs ${formatIsoDate(meta.comparePeriodStart)} → ${formatIsoDate(meta.comparePeriodEnd ?? meta.periodStart)}`
    : "";

  const coverSection = `
    <section class="page cover">
      <div class="hero-label">Social sessions</div>
      <div class="hero-number">${formatNumber(overview.sessions)}</div>
      <div class="property-name">${escapeHtml(propertyLabel)} <em>at a glance</em></div>
      <div class="period">${escapeHtml(periodLabel)}${compareLabel ? " " + escapeHtml(compareLabel) : ""}</div>
      <div class="footer-row">
        <a href="${escapeHtml(PDF_COVER_LOGO_HREF)}" class="sked-mark" aria-label="Sked Social">${SKED_LOGO_IMG}</a>
      </div>
    </section>
  `;

  // Use a real HTML <table> with <colgroup> + explicit percentage widths.
  // Prince's table layout engine honours <col width> deterministically,
  // which \`display: table-cell; width: 25%\` does not — border-spacing
  // and intrinsic content sizing can bleed in otherwise.
  const colCount = 3 + (hasCustomConversions ? 1 : 0) + (showRevenue ? 1 : 0);
  const colWidth = `${(100 / colCount).toFixed(2)}%`;
  const cols = Array.from({ length: colCount }, () => `<col style="width: ${colWidth};" />`).join(
    "",
  );
  const kpiGrid = `
    <table class="kpi-grid ${colCount <= 3 ? "three" : ""}">
      <colgroup>${cols}</colgroup>
      <tr>
        <td class="kpi">
          <div class="kpi-card">
            <div class="label">Sessions</div>
            <div class="value">${formatCompactNumber(overview.sessions)}</div>
            ${compareActive ? renderDelta(overview.deltas.sessions) : ""}
          </div>
        </td>
        <td class="kpi">
          <div class="kpi-card">
            <div class="label">Engagement rate</div>
            <div class="value">${formatPercent(overview.engagementRate)}</div>
            ${
              compareActive
                ? renderDelta(
                    overview.deltas.engagementRate === undefined
                      ? null
                      : overview.deltas.engagementRate,
                  )
                : ""
            }
          </div>
        </td>
        <td class="kpi">
          <div class="kpi-card">
            <div class="label">Conversions</div>
            <div class="value">${formatCompactNumber(overview.keyEvents)}</div>
            ${compareActive ? renderDelta(overview.deltas.keyEvents) : ""}
          </div>
        </td>
        ${
          hasCustomConversions
            ? `<td class="kpi">
                <div class="kpi-card">
                  <div class="label">Custom conversions</div>
                  <div class="value">${formatCompactNumber(overview.customConversions ?? 0)}</div>
                  ${compareActive ? renderDelta(overview.deltas.customConversions ?? null) : ""}
                </div>
              </td>`
            : ""
        }
        ${
          showRevenue
            ? `<td class="kpi">
                <div class="kpi-card">
                  <div class="label">Revenue</div>
                  <div class="value">${formatCompactCurrency(overview.totalRevenue)}</div>
                  ${compareActive ? renderDelta(overview.deltas.totalRevenue) : ""}
                </div>
              </td>`
            : ""
        }
      </tr>
    </table>
  `;

  const execSummary = recommendations.length
    ? `
      <section class="exec-summary">
        <div class="section-header">
          <div class="eyebrow">What to do next</div>
          <span class="accent-bar"></span>
          <h2>Important <em>actions</em> to consider</h2>
        </div>
        <ul>
          ${recommendations
            .map(
              (r) => `
            <li>
              <span class="severity ${escapeHtml(r.severity)}"></span>
              <div>
                <div class="cat">${escapeHtml(r.category)}</div>
                <div class="msg">${escapeHtml(r.message)}</div>
              </div>
            </li>
          `,
            )
            .join("")}
        </ul>
      </section>
    `
    : "";

  // M12.5 — Trend page. Sums sessions per date across every platform
  // into a single-series line, with a small "platforms × totals over
  // the window" table below for the tabular view. Pure static HTML/SVG
  // — Prince 10.1 renders the inline SVG without JS.
  const trendSection = buildTrendSection(payload, propertyLabel, periodLabel, FOOTER_LOGO);

  // M13.1 — Source mix page. Sits between the trend page and the
  // platform leaderboard so the reader sees the macro picture (channels)
  // before the social-only deep dive (platforms).
  const sourceMixSection = buildSourceMixSection(payload, propertyLabel, periodLabel, FOOTER_LOGO);

  // M13.2 — Depth page. Mounts directly after Source mix so the macro
  // mix → depth narrative continues into the platform leaderboard.
  const depthSection = buildDepthSection(payload, propertyLabel, periodLabel, FOOTER_LOGO);

  // M13.3 — Mobile page. Sits directly after the platform leaderboard
  // in the body sequence below, mirroring the dashboard placement.
  const mobileSection = buildMobileSection(payload, propertyLabel, periodLabel, FOOTER_LOGO);

  // M13.4 — Ecommerce page. Mirrors the dashboard placement: after the
  // landing pages table, before the country breakdown. Conditional on
  // probe-pass + windowActive — pre-M13.4 cached payloads return "".
  const ecommerceSection = buildEcommerceSection(payload, propertyLabel, periodLabel, FOOTER_LOGO);

  const platformTable = perPlatform.length
    ? `
      <section class="page">
        <div class="section-header">
          <div class="eyebrow">Platforms</div>
          <span class="accent-bar"></span>
          <h2>Where <em>social traffic</em> is coming from</h2>
        </div>
        <table class="data">
          <thead><tr>
            <th>Platform</th>
            <th style="text-align:right;">Sessions</th>
            <th style="text-align:right;">Engaged</th>
            <th style="text-align:right;">Conversions</th>
            ${hasCustomConversions ? '<th style="text-align:right;">Custom conversions</th>' : ""}
            ${showRevenue ? '<th style="text-align:right;">Revenue</th>' : ""}
          </tr></thead>
          <tbody>
            ${perPlatform
              .slice(0, 10)
              .map(
                (p) => `
              <tr>
                <td><strong>${escapeHtml(p.platform)}</strong></td>
                <td style="text-align:right;">${formatNumber(p.sessions)}</td>
                <td style="text-align:right;">${formatPercent(p.sessions > 0 ? p.engagedSessions / p.sessions : 0)}</td>
                <td style="text-align:right;">${formatNumber(p.keyEvents)}</td>
                ${hasCustomConversions ? `<td style="text-align:right;">${formatNumber(p.customConversions ?? 0)}</td>` : ""}
                ${showRevenue ? `<td style="text-align:right;">${formatCurrency(p.totalRevenue)}</td>` : ""}
              </tr>
            `,
              )
              .join("")}
          </tbody>
        </table>
        <div class="print-footer">
          <span>${escapeHtml(propertyLabel)} · ${escapeHtml(periodLabel)}</span>
          ${FOOTER_LOGO}
        </div>
      </section>
    `
    : "";

  const landingTable = landingPages.length
    ? `
      <section class="page">
        <div class="section-header">
          <div class="eyebrow">Landing pages</div>
          <span class="accent-bar"></span>
          <h2>Pages winning the <em>click</em></h2>
        </div>
        <table class="data">
          <thead><tr>
            <th>Landing page</th>
            <th>Top platform</th>
            <th style="text-align:right;">Sessions</th>
            <th style="text-align:right;">Conversions</th>
            ${hasCustomConversions ? '<th style="text-align:right;">Custom conversions</th>' : ""}
          </tr></thead>
          <tbody>
            ${landingPages
              .slice(0, 30)
              .map(
                (lp) => `
              <tr>
                <td style="font-family:ui-monospace,Menlo,Consolas,monospace;font-size:9pt;">${escapeHtml(lp.landingPage)}</td>
                <td>${escapeHtml(lp.platform)}</td>
                <td style="text-align:right;">${formatNumber(lp.sessions)}</td>
                <td style="text-align:right;">${formatNumber(lp.keyEvents)}</td>
                ${hasCustomConversions ? `<td style="text-align:right;">${formatNumber(lp.customConversions ?? 0)}</td>` : ""}
              </tr>
            `,
              )
              .join("")}
          </tbody>
        </table>
        <div class="print-footer">
          <span>${escapeHtml(propertyLabel)} · ${escapeHtml(periodLabel)}</span>
          ${FOOTER_LOGO}
        </div>
      </section>
    `
    : "";

  const countryTable = perCountry.length
    ? `
      <section class="page">
        <div class="section-header">
          <div class="eyebrow">Geography</div>
          <span class="accent-bar"></span>
          <h2>Where in the <em>world</em></h2>
        </div>
        <table class="data">
          <thead><tr>
            <th>Country</th>
            <th style="text-align:right;">Sessions</th>
            <th style="text-align:right;">Conversions</th>
            ${hasCustomConversions ? '<th style="text-align:right;">Custom conversions</th>' : ""}
          </tr></thead>
          <tbody>
            ${perCountry
              .slice(0, 25)
              .map(
                (c) => `
              <tr>
                <td><strong>${escapeHtml(c.country || "(unknown)")}</strong></td>
                <td style="text-align:right;">${formatNumber(c.sessions)}</td>
                <td style="text-align:right;">${formatNumber(c.keyEvents)}</td>
                ${hasCustomConversions ? `<td style="text-align:right;">${formatNumber(c.customConversions ?? 0)}</td>` : ""}
              </tr>
            `,
              )
              .join("")}
          </tbody>
        </table>
        <div class="print-footer">
          <span>${escapeHtml(propertyLabel)} · ${escapeHtml(periodLabel)}</span>
          ${FOOTER_LOGO}
        </div>
      </section>
    `
    : "";

  // M13.6 / SKE-3454 — Insights page. Sits directly after the overview
  // page (and before the trend / sourceMix / depth / platforms cluster)
  // so the PDF reader sees the friendly summary up front, then drills
  // in. When V2 is present we lead with `shareableTLDR` as a lead
  // subhead, then expandedParagraphs, then the action list — the PDF
  // is the artifact, so we keep everything that's useful.
  const insightsSection = buildInsightsSection(
    insightsV2,
    insights,
    propertyLabel,
    periodLabel,
    FOOTER_LOGO,
    periodPossessiveForWindow(meta.periodStart, meta.periodEnd),
  );

  const body = `
    ${coverSection}
    <section class="page">
      <div class="section-header">
        <div class="eyebrow">Traffic report</div>
        <span class="accent-bar"></span>
        <h2>Social traffic <em>at a glance</em></h2>
      </div>
      ${kpiGrid}
      ${execSummary}
      <div class="print-footer">
        <span>${escapeHtml(propertyLabel)} · ${escapeHtml(periodLabel)}</span>
        ${FOOTER_LOGO}
      </div>
    </section>
    ${insightsSection}
    ${trendSection}
    ${sourceMixSection}
    ${depthSection}
    ${platformTable}
    ${mobileSection}
    ${landingTable}
    ${ecommerceSection}
    ${countryTable}
  `;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(propertyLabel)} — Traffic Snapshot</title>
  <link rel="stylesheet" href="https://cloud.typography.com/7376694/6658812/css/fonts.css">
  <style>${PRINT_CSS}</style>
</head>
<body>${body}</body>
</html>`;
}

// M13.6 / SKE-3454 — Insights page renderer.
//
// Three render paths, in priority order:
//   1. V2 present: lead subhead = `summary.shareableTLDR ?? headline`,
//      body = expandedParagraphs, then the full action list as a
//      numbered block. The PDF gets EVERYTHING — the email is the
//      teaser, the PDF is the artifact.
//   2. Legacy present but no V2 (pre-SKE-3454 cached payloads): render
//      the legacy paragraph + bullets exactly as before so old exports
//      don't regress visually.
//   3. Nothing: placeholder copy so the PDF still ships a recognisable
//      page in the right slot.
function buildInsightsSection(
  insightsV2: InsightsV2 | null,
  legacy: { paragraph: string; bullets: string[] } | null,
  propertyLabel: string,
  periodLabel: string,
  footerLogoHtml: string,
  periodPossessive: string,
): string {
  let bodyHtml: string;
  if (insightsV2) {
    const tldr = insightsV2.summary.shareableTLDR?.trim() || insightsV2.headline.trim();
    const expandedHtml = insightsV2.summary.expandedParagraphs
      .map(
        (p, i, arr) =>
          `<p style="font-size:13pt;line-height:1.55;color:#0F0F14;margin:0 0 ${i === arr.length - 1 ? 18 : 10}pt;">${escapeHtml(p)}</p>`,
      )
      .join("");
    const actionsHtml =
      insightsV2.actions.length > 0
        ? `<div style="margin-top:18pt;">
             <div style="font-size:10pt;font-weight:700;letter-spacing:0.14em;text-transform:uppercase;color:#4F31FF;margin-bottom:8pt;">Actions this period</div>
             <ol style="margin:0;padding-left:18pt;color:#0F0F14;">${insightsV2.actions
               .map(
                 (a) =>
                   `<li style="margin:8pt 0;font-size:11pt;line-height:1.55;">
                      <strong style="font-weight:700;">${escapeHtml(a.title)}</strong>
                      <span style="color:#6B7280;"> — ${escapeHtml(a.evidence)}</span>
                    </li>`,
               )
               .join("")}</ol>
           </div>`
        : "";
    bodyHtml = `
      <p style="font-size:16pt;line-height:1.45;color:#0F0F14;font-weight:600;margin:0 0 18pt;border-left:3pt solid #4F31FF;padding-left:12pt;">${escapeHtml(tldr)}</p>
      ${expandedHtml}
      ${actionsHtml}
    `;
  } else if (legacy && (legacy.paragraph || legacy.bullets.length > 0)) {
    const paragraphsHtml = legacy.paragraph
      ? splitInsightsParagraphs(legacy.paragraph)
          .map(
            (p, i, arr) =>
              `<p style="font-size:14pt;line-height:1.55;color:#0F0F14;margin:0 0 ${i === arr.length - 1 ? 18 : 10}pt;">${renderInsightsEmphasis(p)}</p>`,
          )
          .join("")
      : "";
    bodyHtml = `
      ${paragraphsHtml}
      ${
        legacy.bullets.length > 0
          ? `<ul style="margin:0;padding-left:18pt;color:#0F0F14;">${legacy.bullets
              .map(
                (b) =>
                  `<li style="margin:6pt 0;font-size:11pt;line-height:1.55;">${renderInsightsEmphasis(b)}</li>`,
              )
              .join("")}</ul>`
          : ""
      }
    `;
  } else {
    bodyHtml = `<p style="font-size:11pt;line-height:1.55;color:#6B7280;margin:0;">
        AI summary temporarily unavailable for this export. The data pages
        below cover the full picture.
      </p>`;
  }
  return `
    <section class="page">
      <div class="section-header">
        <div class="eyebrow">AI insights</div>
        <span class="accent-bar"></span>
        <h2>What ${escapeHtml(periodPossessive)} data is <em>telling you</em></h2>
      </div>
      <div style="margin-top:14pt;">${bodyHtml}</div>
      <div class="print-footer">
        <span>${escapeHtml(propertyLabel)} · ${escapeHtml(periodLabel)}</span>
        ${footerLogoHtml}
      </div>
    </section>
  `;
}

// Hex equivalents of the per-platform tokens in PlatformBadge / the
// live TimeSeriesChart. Inlined here so the shared print-html module
// doesn't have to reach into src/client. Keep in sync if those palettes
// change.
const PRINT_PLATFORM_COLOR: Record<string, string> = {
  Instagram: "#D946EF",
  Facebook: "#3B82F6",
  LinkedIn: "#0EA5E9",
  TikTok: "#0A0A0A",
  Pinterest: "#F43F5E",
  YouTube: "#EF4444",
  Threads: "#404040",
  X: "#171717",
  "Google Business": "#F59E0B",
  "Other Social": "#9583FF",
};
const PRINT_ALL_COLOR = "#4F31FF"; // purple-500 — same as the live "All" line

function buildTrendSection(
  payload: ReportPayload,
  propertyLabel: string,
  periodLabel: string,
  footerLogoHtml: string,
): string {
  const { trend, perPlatform } = payload;
  if (trend.length === 0) return "";

  // PDF mirrors the live chart's "All + top platforms overlay" default
  // since there's no UI control on the print side. We always emit the
  // aggregate "All social" line, then layer the top-5 platforms by
  // session volume on top so the reader sees both the overall shape
  // and where the volume actually comes from. Five caps the legend at
  // a readable count without crowding the print width.
  const PRINT_PLATFORM_LINE_LIMIT = 5;

  // Aggregate ("All social") — sum sessions per ISO date across every
  // platform.
  const allByDate = new Map<string, number>();
  for (const row of trend) {
    allByDate.set(row.date, (allByDate.get(row.date) ?? 0) + row.sessions);
  }
  const allPoints = Array.from(allByDate.entries())
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([date, value]) => ({ date, value }));

  // Per-platform — pick the top N platforms by total session volume
  // (matches the leaderboard order, gives the reader the highest-signal
  // overlay lines).
  const platformOrder = perPlatform
    .slice()
    .sort((a, b) => b.sessions - a.sessions)
    .slice(0, PRINT_PLATFORM_LINE_LIMIT)
    .map((p) => p.platform);

  const platformSeries: Array<{
    label: string;
    color: string;
    points: { date: string; value: number }[];
  }> = [];
  for (const platform of platformOrder) {
    const byDate = new Map<string, number>();
    for (const row of trend) {
      if (row.platform !== platform) continue;
      byDate.set(row.date, (byDate.get(row.date) ?? 0) + row.sessions);
    }
    const points = Array.from(byDate.entries())
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .map(([date, value]) => ({ date, value }));
    platformSeries.push({
      label: platform,
      color: PRINT_PLATFORM_COLOR[platform] ?? PRINT_ALL_COLOR,
      points,
    });
  }

  // Series array passed to the SVG builder — aggregate first so it
  // sits visually on top in the rendered legend, matching the live
  // chart's render order.
  const seriesArr = [
    { label: "All social", color: PRINT_ALL_COLOR, points: allPoints },
    ...platformSeries,
  ];

  // Granularity hint — day if ≤ 30 points, week ≤ 120, else month.
  // Driven by the aggregate's point count since per-platform series
  // share the same date grid.
  const granularity = allPoints.length <= 30 ? "day" : allPoints.length <= 120 ? "week" : "month";

  const svg = buildTimeSeriesSvg(
    seriesArr.map((s) => s.points),
    {
      width: 680,
      height: 240,
      colors: seriesArr.map((s) => s.color),
      yLabel: "Sessions",
      granularity,
    },
  );

  // Legend chips — one per series so the reader can match colour to
  // platform. Mirrors the live chart's chip row.
  const legendHtml = seriesArr
    .map(
      (s) => `
        <span style="display:inline-flex;align-items:center;gap:5pt;font-size:9pt;color:#374151;margin-right:14pt;">
          <span style="display:inline-block;width:8pt;height:8pt;border-radius:2pt;background:${s.color};"></span>
          ${escapeHtml(s.label)}
        </span>`,
    )
    .join("");

  // Tabular view — top platforms × totals over the window. Mirrors the
  // dashboard Totals-per-platform view so the PDF reader has both.
  const platformTotals = perPlatform
    .slice()
    .sort((a, b) => b.sessions - a.sessions)
    .slice(0, 8);

  return `
    <section class="page">
      <div class="section-header">
        <div class="eyebrow">Trend</div>
        <span class="accent-bar"></span>
        <h2>How social traffic <em>moves</em> over time</h2>
      </div>
      <div style="margin:12pt 0 6pt;">${svg}</div>
      <div style="margin:0 0 8pt;">${legendHtml}</div>
      <p style="font-size:9pt;color:#6B7280;margin:0 0 14pt;">
        Per-${granularity} sessions: the aggregate "All social" line plus the top
        ${platformSeries.length} platform${platformSeries.length === 1 ? "" : "s"} by volume.
      </p>
      <table class="data">
        <thead><tr>
          <th>Platform</th>
          <th style="text-align:right;">Sessions (window total)</th>
          <th style="text-align:right;">Share</th>
        </tr></thead>
        <tbody>
          ${platformTotals
            .map(
              (p) => `
            <tr>
              <td><strong>${escapeHtml(p.platform)}</strong></td>
              <td style="text-align:right;">${formatNumber(p.sessions)}</td>
              <td style="text-align:right;">${formatPercent(trendTotalShare(p.sessions, platformTotals))}</td>
            </tr>
          `,
            )
            .join("")}
        </tbody>
      </table>
      <div class="print-footer">
        <span>${escapeHtml(propertyLabel)} · ${escapeHtml(periodLabel)}</span>
        ${footerLogoHtml}
      </div>
    </section>
  `;
}

function trendTotalShare(sessions: number, rows: ReadonlyArray<{ sessions: number }>): number {
  const total = rows.reduce((acc, r) => acc + r.sessions, 0);
  if (total === 0) return 0;
  return sessions / total;
}

// M13.1 — Source mix PDF page. 100% normalised stacked area chart on top
// (top 8 channels by sessions + an "Other" rollup band), a "Social share"
// callout to the right of the chart, and a per-channel datatable showing
// every one of the 15 GA4 channel-group buckets verbatim.
function buildSourceMixSection(
  payload: ReportPayload,
  propertyLabel: string,
  periodLabel: string,
  footerLogoHtml: string,
): string {
  const { sourceMix } = payload;
  if (!sourceMix || sourceMix.perChannel.length === 0) return "";

  const totalSessions = sourceMix.perChannel.reduce((acc, r) => acc + r.sessions, 0);

  // Granularity hint — same rule as the dashboard control: ≤30 days
  // daily, 31-120 weekly, otherwise monthly. Counted off the trend
  // bucket span rather than `meta` so the chart matches the data
  // density even when the underlying preset rolls over.
  const dateSet = new Set<string>(sourceMix.trend.map((r) => r.date));
  const dateCount = dateSet.size || 1;
  const granularity = dateCount <= 30 ? "day" : dateCount <= 120 ? "week" : "month";

  const svg = buildSourceMixSvg(sourceMix.trend, {
    width: 680,
    height: 240,
    granularity,
  });

  const socialShare = sourceMix.socialShare.sessions;
  const socialShareDelta = sourceMix.socialShare.delta;
  const socialShareDeltaLabel =
    socialShareDelta != null && Number.isFinite(socialShareDelta)
      ? `${socialShareDelta >= 0 ? "+" : ""}${(socialShareDelta * 100).toFixed(1)} pp`
      : "—";
  const socialShareDeltaClass =
    socialShareDelta == null
      ? ""
      : socialShareDelta > 0
        ? "up"
        : socialShareDelta < 0
          ? "down"
          : "";

  return `
    <section class="page">
      <div class="section-header">
        <div class="eyebrow">Source mix</div>
        <span class="accent-bar"></span>
        <h2>The wider <em>channel mix</em> driving your site</h2>
      </div>
      <div style="display:flex;align-items:flex-start;gap:14pt;margin-bottom:8pt;">
        <div style="flex:1;">${svg}</div>
        <div style="width:120pt;background:#F4F2FF;border:1px solid #DCD6FF;border-radius:6pt;padding:10pt 12pt;">
          <div class="label">Social share</div>
          <div class="value">${formatPercent(socialShare)}</div>
          ${
            socialShareDeltaClass
              ? `<div class="delta ${socialShareDeltaClass}">${escapeHtml(socialShareDeltaLabel)}</div>`
              : `<div class="delta">${escapeHtml(socialShareDeltaLabel)}</div>`
          }
          <p style="font-size:8pt;color:#6B7280;margin:6pt 0 0;line-height:1.35;">
            Organic + Paid Social as a share of every session.
          </p>
        </div>
      </div>
      <p style="font-size:9pt;color:#6B7280;margin:0 0 14pt;">
        100% stacked area: the top 8 channels by sessions; everything else collapsed into a grey
        &ldquo;Other&rdquo; band. Table below shows every bucket verbatim.
      </p>
      <table class="data">
        <thead><tr>
          <th>Channel</th>
          <th style="text-align:right;">Sessions</th>
          <th style="text-align:right;">Share</th>
          <th style="text-align:right;">Active users</th>
          <th style="text-align:right;">Total users</th>
          <th style="text-align:right;">Pageviews</th>
        </tr></thead>
        <tbody>
          ${sourceMix.perChannel
            .map(
              (r) => `
            <tr>
              <td><strong>${escapeHtml(r.channel)}</strong></td>
              <td style="text-align:right;">${formatNumber(r.sessions)}</td>
              <td style="text-align:right;">${formatPercent(totalSessions > 0 ? r.sessions / totalSessions : 0)}</td>
              <td style="text-align:right;">${formatNumber(r.activeUsers)}</td>
              <td style="text-align:right;">${formatNumber(r.totalUsers)}</td>
              <td style="text-align:right;">${formatNumber(r.pageviews)}</td>
            </tr>
          `,
            )
            .join("")}
        </tbody>
      </table>
      <div class="print-footer">
        <span>${escapeHtml(propertyLabel)} · ${escapeHtml(periodLabel)}</span>
        ${footerLogoHtml}
      </div>
    </section>
  `;
}

// M13.2 — Depth PDF page. Dual-axis chart on top (pageviews left,
// active users right), per-date datatable below. Mirrors what the
// dashboard DepthSection renders so the static PDF tells the same
// story without JS.
function buildDepthSection(
  payload: ReportPayload,
  propertyLabel: string,
  periodLabel: string,
  footerLogoHtml: string,
): string {
  const { trend, overview } = payload;
  if (trend.length === 0) return "";

  // Sum per-(date, platform) trend rows down to per-date depth points.
  // The PDF doesn't honour user granularity overrides (DocRaptor renders
  // a single static doc) — we use the same smart-default rule the
  // dashboard does so the chart density matches the typical view.
  const points: DepthSvgPoint[] = [];
  const accs = new Map<
    string,
    { pageviews: number; activeUsers: number; totalUsers: number; sessions: number }
  >();
  for (const r of trend) {
    const acc = accs.get(r.date) ?? {
      pageviews: 0,
      activeUsers: 0,
      totalUsers: 0,
      sessions: 0,
    };
    acc.pageviews += r.pageviews;
    acc.activeUsers += r.activeUsers;
    acc.totalUsers += r.totalUsers;
    acc.sessions += r.sessions;
    accs.set(r.date, acc);
  }
  const tableRows: Array<{
    date: string;
    pageviews: number;
    activeUsers: number;
    totalUsers: number;
    pagesPerSession: number;
  }> = [];
  for (const [date, vals] of Array.from(accs.entries()).sort((a, b) => (a[0] < b[0] ? -1 : 1))) {
    points.push({ date, pageviews: vals.pageviews, activeUsers: vals.activeUsers });
    tableRows.push({
      date,
      pageviews: vals.pageviews,
      activeUsers: vals.activeUsers,
      totalUsers: vals.totalUsers,
      pagesPerSession: vals.sessions > 0 ? vals.pageviews / vals.sessions : 0,
    });
  }

  const dateCount = points.length || 1;
  const granularity = dateCount <= 30 ? "day" : dateCount <= 120 ? "week" : "month";
  const svg = buildDepthSvg(points, { width: 680, height: 240, granularity });

  return `
    <section class="page">
      <div class="section-header">
        <div class="eyebrow">Depth</div>
        <span class="accent-bar"></span>
        <h2>How <em>deeply</em> social visitors explore</h2>
      </div>
      <div style="display:flex;align-items:flex-start;gap:14pt;margin-bottom:8pt;">
        <div style="flex:1;">${svg}</div>
        <div style="width:130pt;background:#F4F2FF;border:1px solid #DCD6FF;border-radius:6pt;padding:10pt 12pt;">
          <div class="label">Pages / session</div>
          <div class="value">${overview.pagesPerSession.toFixed(2)}</div>
          ${
            overview.deltas.pagesPerSession != null
              ? `<div class="delta ${overview.deltas.pagesPerSession >= 0 ? "up" : "down"}">${formatDelta(overview.deltas.pagesPerSession)}</div>`
              : `<div class="delta">—</div>`
          }
          <p style="font-size:8pt;color:#6B7280;margin:6pt 0 0;line-height:1.35;">
            Average pages a social visitor opens per session over the window.
          </p>
        </div>
      </div>
      <p style="font-size:9pt;color:#6B7280;margin:0 0 14pt;">
        Pageviews on the left axis (filled blurple area) vs active users on the right axis (dashed
        purple line). The wider the gap, the more pages each visitor is opening.
      </p>
      <table class="data">
        <thead><tr>
          <th>Date</th>
          <th style="text-align:right;">Pageviews</th>
          <th style="text-align:right;">Active users</th>
          <th style="text-align:right;">Total users</th>
          <th style="text-align:right;">Pages / session</th>
        </tr></thead>
        <tbody>
          ${tableRows
            .slice(0, 30)
            .map(
              (r) => `
            <tr>
              <td><strong>${escapeHtml(formatDateForPdf(r.date))}</strong></td>
              <td style="text-align:right;">${formatNumber(r.pageviews)}</td>
              <td style="text-align:right;">${formatNumber(r.activeUsers)}</td>
              <td style="text-align:right;">${formatNumber(r.totalUsers)}</td>
              <td style="text-align:right;">${r.pagesPerSession.toFixed(2)}</td>
            </tr>
          `,
            )
            .join("")}
        </tbody>
      </table>
      <div class="print-footer">
        <span>${escapeHtml(propertyLabel)} · ${escapeHtml(periodLabel)}</span>
        ${footerLogoHtml}
      </div>
    </section>
  `;
}

function formatDateForPdf(iso: string): string {
  try {
    return formatTableRowDate(iso);
  } catch {
    return iso;
  }
}

// Period-possessive for the PDF's AI-insights page heading. Mirrors
// the client-side `periodPossessive()` in InsightsSection.tsx so the
// PDF + dashboard + share viewer all pick the same word.
function periodPossessiveForWindow(startDate: string, endDate: string): string {
  try {
    const days = dayCount(startDate, endDate);
    if (days <= 7) return "this week's";
    if (days <= 32) return "this month's";
    if (days <= 100) return "this quarter's";
    return "this period's";
  } catch {
    return "this period's";
  }
}

// M13.3 — Mobile vs non-mobile PDF page. Compact donut on the right
// (current mix), 100% stacked area on the left (per-day trend), and a
// per-device datatable below. Renders nothing when the payload has no
// mobile block (legacy cached payloads built before M13.3) or when the
// social cohort had zero device rows in the window.
function buildMobileSection(
  payload: ReportPayload,
  propertyLabel: string,
  periodLabel: string,
  footerLogoHtml: string,
): string {
  const { mobile, overview } = payload;
  if (!mobile || mobile.perDevice.length === 0) return "";

  const totalSessions = mobile.perDevice.reduce((acc, r) => acc + r.sessions, 0);
  const showRevenue = overview.totalRevenue > 0;

  // Donut: render every device with non-zero sessions in the canonical
  // Desktop / Mobile / Tablet / Other order. The shared SVG builder
  // skips zero-session slices so a Desktop-only property doesn't draw a
  // hairline arc.
  const donutSvg = buildDeviceMixDonutSvg(
    mobile.perDevice.map((r) => ({ device: r.device, sessions: r.sessions })),
    { size: 140 },
  );

  // Stacked area: granularity inferred from the mobile-trend date span
  // so the chart density matches the data rather than the report-window
  // preset (a 90-day window with 30-day data still reads as daily).
  const dateSet = new Set<string>(mobile.trend.map((r) => r.date));
  const dateCount = dateSet.size || 1;
  const granularity = dateCount <= 30 ? "day" : dateCount <= 120 ? "week" : "month";
  const areaSvg = buildDeviceMixAreaSvg(mobile.trend, {
    width: 480,
    height: 220,
    granularity,
  });

  // Render the perDevice rows in canonical order (rather than the
  // sessions-DESC order the aggregator emits) so multi-property exports
  // read consistently.
  const perDeviceLookup = new Map(mobile.perDevice.map((r) => [r.device, r] as const));
  const orderedRows = DEVICE_RENDER_ORDER.flatMap((d) => {
    const row = perDeviceLookup.get(d);
    return row ? [row] : [];
  });

  const legendChips = orderedRows
    .map((r) => {
      const share = totalSessions > 0 ? r.sessions / totalSessions : 0;
      return `
        <span style="display:inline-flex;align-items:center;gap:4pt;font-size:9pt;color:#374151;margin-right:10pt;">
          <span style="display:inline-block;width:8pt;height:8pt;border-radius:1pt;background:${escapeHtml(DEVICE_COLOR[r.device])};"></span>
          <strong style="font-weight:700;color:#0F0F14;">${escapeHtml(DEVICE_LABEL[r.device])}</strong>
          <span style="font-variant-numeric:tabular-nums;color:#6B7280;">${(share * 100).toFixed(1)}%</span>
        </span>
      `;
    })
    .join("");

  return `
    <section class="page">
      <div class="section-header">
        <div class="eyebrow">Devices</div>
        <span class="accent-bar"></span>
        <h2>How <em>mobile</em> the social audience really is</h2>
      </div>
      <div style="display:flex;align-items:flex-start;gap:14pt;margin-bottom:8pt;">
        <div style="flex:1;">${areaSvg}</div>
        <div style="width:140pt;text-align:center;">
          ${donutSvg}
          <p style="margin:6pt 0 0;font-size:8pt;color:#6B7280;line-height:1.35;">Current-period mix</p>
        </div>
      </div>
      <div style="margin:0 0 12pt;">${legendChips}</div>
      <p style="font-size:9pt;color:#6B7280;margin:0 0 14pt;">
        Desktop / mobile / tablet sessions for the social cohort. The donut summarises the
        current-period mix; the stacked area tracks per-day shifts over the window.
      </p>
      <table class="data">
        <thead><tr>
          <th>Device</th>
          <th style="text-align:right;">Sessions</th>
          <th style="text-align:right;">Share</th>
          <th style="text-align:right;">Active users</th>
          <th style="text-align:right;">Key events</th>
          <th style="text-align:right;">Conv rate</th>
          ${showRevenue ? '<th style="text-align:right;">Revenue</th>' : ""}
        </tr></thead>
        <tbody>
          ${orderedRows
            .map((r) => {
              const share = totalSessions > 0 ? r.sessions / totalSessions : 0;
              return `
            <tr>
              <td><strong>${escapeHtml(DEVICE_LABEL[r.device as DeviceKey])}</strong></td>
              <td style="text-align:right;">${formatNumber(r.sessions)}</td>
              <td style="text-align:right;">${formatPercent(share)}</td>
              <td style="text-align:right;">${formatNumber(r.activeUsers)}</td>
              <td style="text-align:right;">${formatNumber(r.keyEvents)}</td>
              <td style="text-align:right;">${(r.sessionKeyEventRate * 100).toFixed(2)}%</td>
              ${showRevenue ? `<td style="text-align:right;">${formatCurrency(r.totalRevenue)}</td>` : ""}
            </tr>
          `;
            })
            .join("")}
        </tbody>
      </table>
      <div class="print-footer">
        <span>${escapeHtml(propertyLabel)} · ${escapeHtml(periodLabel)}</span>
        ${footerLogoHtml}
      </div>
    </section>
  `;
}

// M13.4 — Ecommerce PDF page. Pure-string SVG stacked-area chart on top
// (per-channel revenue, absolute dollars), per-channel datatable below.
// Renders nothing when ecommerce is null (probe-fail) or windowActive
// is false (probe passed but selected window had zero activity), so
// pre-M13.4 cached payloads + non-ecommerce properties get the same
// untouched PDF as before.
function buildEcommerceSection(
  payload: ReportPayload,
  propertyLabel: string,
  periodLabel: string,
  footerLogoHtml: string,
): string {
  const { ecommerce, meta } = payload;
  if (!ecommerce || !ecommerce.windowActive || ecommerce.perChannel.length === 0) {
    return "";
  }
  const currencyCode = meta.currencyCode ?? null;

  // Granularity hint — same rule as the dashboard control: ≤30 days
  // daily, 31-120 weekly, otherwise monthly. Counted off the trend
  // bucket span rather than `meta` so the chart matches the data
  // density even when the underlying preset rolls over.
  const dateSet = new Set<string>(ecommerce.trend.map((r) => r.date));
  const dateCount = dateSet.size || 1;
  const granularity = dateCount <= 30 ? "day" : dateCount <= 120 ? "week" : "month";

  const svg = buildEcommerceSvg(ecommerce.trend, {
    width: 680,
    height: 240,
    granularity,
    currencyCode,
  });

  const totalRevenue = ecommerce.perChannel.reduce((acc, r) => acc + r.revenue, 0);
  const totalPurchases = ecommerce.perChannel.reduce((acc, r) => acc + r.purchases, 0);
  const overallAOV = totalPurchases > 0 ? totalRevenue / totalPurchases : 0;

  const currencyNote = currencyCode
    ? `Figures shown in <strong>${escapeHtml(currencyCode)}</strong> (resolved from this property's GA4 settings).`
    : "Figures shown without currency formatting (GA4 Admin API was unreachable).";

  return `
    <section class="page">
      <div class="section-header">
        <div class="eyebrow">Ecommerce</div>
        <span class="accent-bar"></span>
        <h2>Revenue your <em>channels</em> are driving</h2>
      </div>
      <div style="display:flex;align-items:flex-start;gap:14pt;margin-bottom:8pt;">
        <div style="flex:1;">${svg}</div>
        <div style="width:140pt;background:#F4F2FF;border:1px solid #DCD6FF;border-radius:6pt;padding:10pt 12pt;">
          <div class="label">Total revenue</div>
          <div class="value">${escapeHtml(formatPdfCurrency(totalRevenue, currencyCode))}</div>
          <p style="font-size:8pt;color:#6B7280;margin:6pt 0 0;line-height:1.35;">
            <strong>${formatNumber(totalPurchases)}</strong> transactions ·
            AOV <strong>${escapeHtml(formatPdfCurrency(overallAOV, currencyCode))}</strong>
          </p>
        </div>
      </div>
      <p style="font-size:9pt;color:#6B7280;margin:0 0 10pt;">${currencyNote}</p>
      <table class="data">
        <thead><tr>
          <th>Channel</th>
          <th style="text-align:right;">Revenue</th>
          <th style="text-align:right;">Transactions</th>
          <th style="text-align:right;">AOV</th>
          <th style="text-align:right;">Revenue / user</th>
          <th style="text-align:right;">Conv rate</th>
        </tr></thead>
        <tbody>
          ${ecommerce.perChannel
            .map(
              (r) => `
            <tr>
              <td><strong>${escapeHtml(r.channel)}</strong></td>
              <td style="text-align:right;">${escapeHtml(formatPdfCurrency(r.revenue, currencyCode))}</td>
              <td style="text-align:right;">${formatNumber(r.purchases)}</td>
              <td style="text-align:right;">${escapeHtml(formatPdfCurrency(r.averageOrderValue, currencyCode))}</td>
              <td style="text-align:right;">${escapeHtml(formatPdfCurrency(r.revenuePerUser, currencyCode))}</td>
              <td style="text-align:right;">${(r.purchaserConversionRate * 100).toFixed(2)}%</td>
            </tr>
          `,
            )
            .join("")}
        </tbody>
      </table>
      <div class="print-footer">
        <span>${escapeHtml(propertyLabel)} · ${escapeHtml(periodLabel)}</span>
        ${footerLogoHtml}
      </div>
    </section>
  `;
}

/** PDF-side currency formatter — falls back to plain "$NNN" form when
 *  currencyCode is null/malformed (Admin API miss). Pinned to en-US
 *  so the output stays deterministic across rendering hosts. */
function formatPdfCurrency(value: number, currencyCode: string | null | undefined): string {
  if (!Number.isFinite(value)) return "—";
  if (currencyCode && /^[A-Z]{3}$/.test(currencyCode)) {
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currencyCode,
        maximumFractionDigits: 0,
      }).format(value);
    } catch {
      // Fall through.
    }
  }
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(value);
}
